import React, { useState, useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { groupBy } from 'lodash';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import Modal from '../Modal';
import { Flex, FlexColumn } from '../Flex';
import { Header2 } from '../Typography';
import BaseCheckBox from '../CheckBox';

const Column = styled(FlexColumn)`
  margin-right: 55px;
`;

const Category = styled(Header2)`
  margin-bottom: 44px;
`;

const CheckBox = styled(BaseCheckBox)`
  margin-bottom: 16px;
  input[type='checkbox'] {
    display: none;
  }
`;

const ActionButtons = styled(Flex)`
  margin-bottom: 24px;
  gap: 16px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ColumnsMenu = ({
  columnsMenuTitle,
  allColumns,
  visibleColumns,
  onClose,
  updateVisibleColumns,
  resetToDefault,
  showReset = true,
  size,
  renderColumnsMenuExtraData,
  confirmText,
  cancelText
}) => {
  const { t } = useContext(ConfigContext);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    visibleColumns
      .filter(Boolean)
      .map(({ dataKey }) => dataKey)
      .filter(Boolean)
  );
  const categories = useMemo(
    () =>
      groupBy(
        allColumns.filter((column) => column && column.category),
        'category'
      ),
    [allColumns]
  );

  const handleToggleColumn = useCallback(
    (selectedId, checked) => {
      if (!checked) {
        setSelectedColumns(selectedColumns.filter((id) => id !== selectedId));
      } else {
        setSelectedColumns([...selectedColumns, selectedId]);
      }
    },
    [selectedColumns]
  );

  const handleSelectAll = useCallback(() => {
    const allDataKeys = allColumns.filter((column) => column && column.dataKey).map((column) => column.dataKey);
    setSelectedColumns(allDataKeys);
  }, [allColumns]);

  const handleClearAll = useCallback(() => {
    setSelectedColumns([]);
  }, []);

  return (
    <Modal
      size={size}
      onClose={onClose}
      title={columnsMenuTitle}
      confirmText={confirmText}
      onConfirm={() => updateVisibleColumns(selectedColumns)}
      showCancelButton={showReset}
      cancelText={cancelText || t('resetToDefault')}
      onCancel={() => {
        resetToDefault?.();
        onClose();
      }}
      usePortal>
      <FlexColumn>
        {renderColumnsMenuExtraData}
        <ActionButtons>
          <ActionButton onClick={handleSelectAll} type="button">
            {t('selectAll')}
          </ActionButton>
          <ActionButton onClick={handleClearAll} type="button">
            {t('clearAll')}
          </ActionButton>
        </ActionButtons>
        <Flex>
          {Object.entries(categories).map(([category, columnsList]) => (
            <Column key={category}>
              {category !== ' ' && <Category>{category}</Category>}

              {columnsList
                .filter((column) => column && column.dataKey)
                .map(({ Header, dataKey, columnListLabel }, index) => (
                  <CheckBox
                    key={`${dataKey}-${index}`}
                    name={dataKey}
                    label={t(columnListLabel || Header)}
                    value={selectedColumns.includes(dataKey)}
                    onChange={(checked) => handleToggleColumn(dataKey, checked)}
                  />
                ))}
            </Column>
          ))}
        </Flex>
      </FlexColumn>
    </Modal>
  );
};

export default ColumnsMenu;
